import { useState, useCallback, useEffect, useRef } from 'react';
import { NOTIFICATION } from '../components/NotificationSystem';
import { createShare } from '../services/shareApi';
import { recordCommunityBuildView, toggleCommunityBuildLike } from '../services/buildApi';
import { canonicalBuildPath, localGenPath, routeSegmentForTab } from '../utils/buildRoutes';
import { DISPLAY_MODE_GENERATOR, DISPLAY_MODE_SIMPLE } from '../core/GenConfig';
import { dofusbookUrl } from '../core/dofusbookLink';
import * as buildStorage from '../services/buildStorage';

const TXT_LOG_IN_TO_LIKE = { fr: 'Connecte-toi pour liker.', en: 'Log in to like.', de: 'Melde dich an, um zu liken.', es: 'Inicia sesión para dar like.', pt: 'Entre para curtir.' };
const TXT_LIKE_FAILED = { fr: 'Echec du like.', en: 'Like failed.', de: 'Like fehlgeschlagen.', es: 'No se ha podido dar like.', pt: 'Não foi possível curtir.' };

const MY_BUILDS_ROUTE = 'my-builds';

const MSG_BUILD_COPIED = { fr: 'Stuff dupliqué.', en: 'Build duplicated.', de: 'Build dupliziert.', es: 'Build duplicada.', pt: 'Build duplicada.' };
const MSG_BUILD_DELETED = { fr: 'Stuff supprimé.', en: 'Build deleted.', de: 'Build gelöscht.', es: 'Build eliminada.', pt: 'Build excluída.' };
const MSG_BUILD_IMPORTED = { fr: 'Stuff importé.', en: 'Build imported.', de: 'Build importiert.', es: 'Build importada.', pt: 'Build importada.' };
const MSG_CONFIRM_DELETE = { fr: 'Supprimer ce stuff ?', en: 'Delete this build?', de: 'Diesen Build löschen?', es: '¿Eliminar esta build?', pt: 'Excluir esta build?' };
const MSG_LINK_COPIED = { fr: 'Lien copié dans le presse-papiers.', en: 'Link copied to clipboard.', de: 'Link in die Zwischenablage kopiert.', es: 'Enlace copiado al portapapeles.', pt: 'Link copiado para a área de transferência.' };
const MSG_EXPORT_FAIL = { fr: "Impossible d'ouvrir l'export Dofusbook.", en: 'Could not open the Dofusbook export.', de: 'Dofusbook-Export konnte nicht geöffnet werden.', es: 'No se ha podido abrir la exportación a Dofusbook.', pt: 'Não foi possível abrir a exportação para o Dofusbook.' };
const MSG_SHARE_FAIL = { fr: 'Impossible de créer le lien de partage.', en: 'Could not create share link.', de: 'Teilen-Link konnte nicht erstellt werden.', es: 'No se ha podido crear el enlace para compartir.', pt: 'Não foi possível criar o link de compartilhamento.' };

/**
 * Build identity for the Generator page — routing/canonical-URL bootstrap,
 * create/duplicate/import/delete/share/rename actions, and the build-name
 * input focus/select behavior. See docs/generator-slicing.md cluster E.
 *
 * Composes with `useGeneratorContext()` rather than duplicating it: callers
 * still call the context hook in Generator.js and pass the pieces this hook
 * needs (`activeBuildId`, `deleteBuild`, `createBuild`, `duplicateBuild`,
 * `openBuild`, `exportState`, `getActiveBuildPath`, `routeBootstrap`,
 * `openCanonicalBuild`, `updateCurrentBuildMeta`, `isAuthenticated`, ...) as
 * params, matching the pattern already used by `useBuildPanelControls`/
 * `useCriteriaAndScore`.
 *
 * The data tab → route segment mapping comes from `utils/buildRoutes.js` (the
 * shared source of truth) rather than being re-declared locally — Generator.js's
 * former local copy was only ever read by this cluster's code.
 */
export function useBuildIdentityActions({
    navigate,
    location,
    lang,
    userSlug,
    buildCode,
    build,
    buildName,
    genConfig,
    setGenConfig,
    activeBuildId,
    isReadOnly,
    isPublicReadOnly,
    canEditBuild,
    isGenerationActive,
    activeDataTab,
    savedDisplayMode,
    currentBuildMeta,
    updateCurrentBuildMeta,
    isAuthenticated,
    deleteBuild,
    createBuild,
    duplicateBuild,
    openBuild,
    exportState,
    getActiveBuildPath,
    routeBootstrap,
    openCanonicalBuild,
    showNotification,
}) {
    const [shareBusy, setShareBusy] = useState(false);
    const [routeNotFound, setRouteNotFound] = useState(false);
    const buildNameInputRef = useRef(null);

    /**
     * Both share paths end here: the link goes on the clipboard, and the
     * notification says so. When the write fails — an insecure origin, a
     * browser that refuses — the notification carries the link itself, which
     * is the only copy the user gets.
     */
    const copyShareUrl = useCallback(async (url) => {
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            showNotification(url, NOTIFICATION.INFO);
            return;
        }
        showNotification(MSG_LINK_COPIED[lang] || MSG_LINK_COPIED.en, NOTIFICATION.SUCCESS);
    }, [lang, showNotification]);

    /**
     * Dofusbook has no import API: the build travels in the URL itself, so the
     * export is just a link to open. Opened without `noopener` so a blocked
     * popup is detectable — this is the one place a build can leave.
     */
    const handleExportBuild = useCallback(() => {
        if (!build) return;
        let href;
        try {
            href = dofusbookUrl(build);
        } catch (e) {
            console.warn('dofusbook export failed', e);
            showNotification(MSG_EXPORT_FAIL[lang] || MSG_EXPORT_FAIL.en, NOTIFICATION.ERROR);
            return;
        }
        const tab = window.open(href, '_blank');
        if (tab) {
            try { tab.opener = null; } catch { /* ignore */ }
            return;
        }
        showNotification(MSG_EXPORT_FAIL[lang] || MSG_EXPORT_FAIL.en, NOTIFICATION.ERROR);
    }, [build, lang, showNotification]);

    const handleHeaderLikeToggle = useCallback(async () => {
        if (!activeBuildId || currentBuildMeta?.scope !== 'remote') return;
        const hadLiked = !!currentBuildMeta?.viewer?.hasLiked;
        if (!isAuthenticated) {
            showNotification(TXT_LOG_IN_TO_LIKE[lang] || TXT_LOG_IN_TO_LIKE.en, NOTIFICATION.INFO);
            return;
        }

        updateCurrentBuildMeta(prev => ({
            ...prev,
            viewer: { ...(prev.viewer || {}), hasLiked: !hadLiked },
            metrics: {
                ...(prev.metrics || {}),
                likes: Math.max(0, Number(prev?.metrics?.likes || 0) + (hadLiked ? -1 : 1)),
            },
        }));

        try {
            const res = await toggleCommunityBuildLike(activeBuildId);
            updateCurrentBuildMeta(prev => ({
                ...prev,
                viewer: { ...(prev.viewer || {}), hasLiked: !!res.liked },
                metrics: { ...(prev.metrics || {}), likes: Number(res.likes || 0) },
            }));
        } catch {
            updateCurrentBuildMeta(prev => ({
                ...prev,
                viewer: { ...(prev.viewer || {}), hasLiked: hadLiked },
                metrics: {
                    ...(prev.metrics || {}),
                    likes: Math.max(0, Number(prev?.metrics?.likes || 0) + (hadLiked ? 1 : -1)),
                },
            }));
            showNotification(TXT_LIKE_FAILED[lang] || TXT_LIKE_FAILED.en, NOTIFICATION.ERROR);
        }
    }, [activeBuildId, currentBuildMeta?.scope, currentBuildMeta?.viewer?.hasLiked, isAuthenticated, lang, showNotification, updateCurrentBuildMeta]);

    useEffect(() => {
        let active = true;
        const bootstrap = async () => {
            const entry = await routeBootstrap({ userSlug, buildCode });
            if (!active) return;
            if (!entry) {
                if (userSlug && buildCode) {
                    setRouteNotFound(true);
                }
                return;
            }
            setRouteNotFound(false);
            const nextTab = routeSegmentForTab(activeDataTab);
            const canonicalPath = getActiveBuildPath(lang, nextTab);
            if (userSlug && buildCode) {
                if (!entry.publicCode || !entry.owner?.public_slug) {
                    setRouteNotFound(true);
                }
                return;
            }
            if (canonicalPath && location.pathname !== canonicalPath) {
                navigate(canonicalPath, { replace: true });
            }
        };
        bootstrap();
        return () => {
            active = false;
        };
    }, [
        activeDataTab,
        buildCode,
        getActiveBuildPath,
        lang,
        location.pathname,
        navigate,
        routeBootstrap,
        userSlug,
    ]);

    // A view is a build someone opened, not a card that scrolled past. The
    // community grids used to count impressions from an IntersectionObserver,
    // so a creator's numbers climbed with the traffic of the listing page
    // rather than with anyone reading the build. Counted here, on the
    // canonical build route, and once per build per page session — the route
    // effect above re-runs on the /damage tab switch and on login, and the
    // backend's 24h dedupe should not have to absorb that.
    const viewedBuildIds = useRef(new Set());
    useEffect(() => {
        if (!userSlug || !buildCode) return undefined;
        if (!activeBuildId || currentBuildMeta?.scope !== 'remote') return undefined;
        // The loaded build must be the one the URL names: a failed resolve
        // leaves the previously open build in place, and that one was not
        // viewed.
        if (currentBuildMeta?.publicCode !== buildCode) return undefined;
        if (viewedBuildIds.current.has(activeBuildId)) return undefined;
        viewedBuildIds.current.add(activeBuildId);

        let active = true;
        recordCommunityBuildView(activeBuildId).then((res) => {
            if (!active) return;
            updateCurrentBuildMeta(prev => ({
                ...prev,
                metrics: { ...(prev.metrics || {}), views: Number(res?.views || 0) },
            }));
        }).catch(() => { });
        return () => { active = false; };
    }, [activeBuildId, buildCode, currentBuildMeta?.publicCode, currentBuildMeta?.scope, updateCurrentBuildMeta, userSlug]);

    // Re-resolve canonical build when auth state changes (owner login upgrades page to editable).
    const prevIsAuthenticatedRef = useRef(isAuthenticated);
    useEffect(() => {
        const wasAuthenticated = prevIsAuthenticatedRef.current;
        prevIsAuthenticatedRef.current = isAuthenticated;
        if (!userSlug || !buildCode) return;
        // Only re-resolve on login (false → true), not on logout (handled by useGeneratorState).
        if (!wasAuthenticated && isAuthenticated) {
            openCanonicalBuild(userSlug, buildCode);
        }
    }, [isAuthenticated, userSlug, buildCode, openCanonicalBuild]);

    const isCopyDisabled = isGenerationActive || !activeBuildId;

    const selectBuildNameInput = useCallback(() => {
        window.requestAnimationFrame(() => {
            const input = buildNameInputRef.current;
            if (!input) return;
            input.focus();
            input.select();
        });
    }, []);

    useEffect(() => {
        if (!location.state?.focusBuildName) return;
        if (isPublicReadOnly || isReadOnly) return;
        selectBuildNameInput();
        const timer = window.setTimeout(() => {
            selectBuildNameInput();
        }, 140);
        return () => window.clearTimeout(timer);
    }, [isPublicReadOnly, isReadOnly, location.state, selectBuildNameInput]);

    const handleCopyBuild = useCallback(async () => {
        if (!activeBuildId) return;
        const copied = await duplicateBuild(activeBuildId, { activate: false });
        if (!copied) return;
        showNotification(MSG_BUILD_COPIED[lang] || MSG_BUILD_COPIED.en, NOTIFICATION.SUCCESS);
        const hasCanonical = !!(copied.publicCode && copied.owner?.public_slug);
        const path = hasCanonical
            ? canonicalBuildPath(lang, copied.owner.public_slug, copied.publicCode)
            : localGenPath(lang);
        if (!hasCanonical) {
            buildStorage.setBootBuildId(copied.id);
        }
        // Omit noopener so we can detect a blocked popup via the return value.
        const tab = window.open(`${window.location.origin}${path}`, '_blank');
        if (tab) {
            try { tab.opener = null; } catch { /* ignore */ }
            return;
        }
        // Popup blocked: fall back to same-tab navigation.
        if (!hasCanonical) buildStorage.consumeBootBuildId();
        await openBuild(copied.id);
        navigate(path);
        selectBuildNameInput();
    }, [activeBuildId, duplicateBuild, lang, navigate, openBuild, selectBuildNameInput, showNotification]);

    const handleCreateBuild = useCallback(async () => {
        const created = await createBuild();
        if (!created) return;
        navigate((created.publicCode && created.owner?.public_slug)
            ? canonicalBuildPath(lang, created.owner.public_slug, created.publicCode)
            : localGenPath(lang));
        selectBuildNameInput();
    }, [createBuild, lang, navigate, selectBuildNameInput]);

    const handleDisplayModeChange = useCallback((mode) => {
        if (!canEditBuild || isGenerationActive) return;
        const nextMode = mode === DISPLAY_MODE_SIMPLE ? DISPLAY_MODE_SIMPLE : DISPLAY_MODE_GENERATOR;
        if (nextMode === savedDisplayMode) return;
        setGenConfig(prev => prev.update('displayMode', nextMode));
    }, [canEditBuild, isGenerationActive, savedDisplayMode, setGenConfig]);

    const handleImportBuild = useCallback(async () => {
        if (!build) return;
        const imported = await createBuild({
            name: buildName || '',
            build: buildStorage.buildToStorage(build),
            genConfig: genConfig?.toObject?.() || {},
        });
        if (!imported) return;
        showNotification(MSG_BUILD_IMPORTED[lang] || MSG_BUILD_IMPORTED.en, NOTIFICATION.SUCCESS);
        navigate((imported.publicCode && imported.owner?.public_slug)
            ? canonicalBuildPath(lang, imported.owner.public_slug, imported.publicCode)
            : localGenPath(lang));
    }, [build, buildName, createBuild, genConfig, lang, navigate, showNotification]);

    const handleShareBuild = useCallback(async () => {
        if (!activeBuildId || isGenerationActive || shareBusy) return;
        const canonicalPath = getActiveBuildPath(lang, routeSegmentForTab(activeDataTab));
        if (canonicalPath) {
            setShareBusy(true);
            try {
                await copyShareUrl(`${window.location.origin}${canonicalPath}`);
            } finally {
                setShareBusy(false);
            }
            return;
        }
        const raw = exportState();
        if (!raw) {
            showNotification(MSG_SHARE_FAIL[lang] || MSG_SHARE_FAIL.en, NOTIFICATION.ERROR);
            return;
        }
        let stateObj;
        try {
            stateObj = JSON.parse(raw);
        } catch {
            showNotification(MSG_SHARE_FAIL[lang] || MSG_SHARE_FAIL.en, NOTIFICATION.ERROR);
            return;
        }
        setShareBusy(true);
        try {
            const slug = await createShare(stateObj);
            await copyShareUrl(`${window.location.origin}/${lang}/s/${slug}`);
        } catch (e) {
            console.warn('share failed', e);
            showNotification(MSG_SHARE_FAIL[lang] || MSG_SHARE_FAIL.en, NOTIFICATION.ERROR);
        } finally {
            setShareBusy(false);
        }
    }, [activeBuildId, activeDataTab, exportState, getActiveBuildPath, isGenerationActive, lang, copyShareUrl, shareBusy, showNotification]);

    const handleDeleteBuild = useCallback(async () => {
        if (!activeBuildId) return;
        if (!window.confirm(MSG_CONFIRM_DELETE[lang] || MSG_CONFIRM_DELETE.en)) return;
        await deleteBuild(activeBuildId);
        showNotification(MSG_BUILD_DELETED[lang] || MSG_BUILD_DELETED.en, NOTIFICATION.SUCCESS);
        navigate(`/${lang}/${MY_BUILDS_ROUTE}`);
    }, [activeBuildId, deleteBuild, lang, navigate, showNotification]);

    return {
        buildNameInputRef,
        shareBusy,
        routeNotFound,
        isCopyDisabled,
        handleHeaderLikeToggle,
        handleCopyBuild,
        handleCreateBuild,
        handleDisplayModeChange,
        handleImportBuild,
        handleShareBuild,
        handleExportBuild,
        handleDeleteBuild,
    };
}
